import { Bell, CheckCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

export default function AdminNotificationBell() {
  const { notifications, unreadCount, unreadByDepartment, loading, markAsRead, markAllAsRead } = useAdminNotifications();
  const navigate = useNavigate();
  const [dept, setDept] = useState<string>("all");
  const depts = Array.from(new Set(notifications.map((n) => n.department || "general")));
  const recent = notifications
    .filter((n) => dept === "all" || (n.department || "general") === dept)
    .slice(0, 15);
  const prevUnreadRef = useRef(unreadCount);
  const initializedRef = useRef(false);
  const [ringing, setRinging] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      prevUnreadRef.current = unreadCount;
      return;
    }
    if (unreadCount > prevUnreadRef.current) {
      setRinging(true);
      const newestUnread = notifications.find((notification) => !notification.read);
      if (newestUnread) {
        toast(newestUnread.title, {
          description: newestUnread.message,
          action: newestUnread.link
            ? {
                label: "Review",
                onClick: () => {
                  markAsRead(newestUnread.id);
                  navigate(newestUnread.link as string);
                },
              }
            : undefined,
        });
      }
      const t = setTimeout(() => setRinging(false), 1500);
      prevUnreadRef.current = unreadCount;
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
  }, [loading, markAsRead, navigate, notifications, unreadCount]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className={`h-5 w-5 transition-transform ${ringing ? "animate-bell-ring" : ""}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 z-50">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="text-sm font-semibold">Admin Notifications</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                markAllAsRead();
              }}
            >
              <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        {depts.length > 1 && (
          <div className="flex flex-wrap gap-1 px-3 pb-2">
            {["all", ...depts].map((d) => (
              <button
                key={d}
                onClick={(e) => { e.preventDefault(); setDept(d); }}
                className={`rounded-full border px-2 py-0.5 text-[11px] capitalize ${dept === d ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
              >
                {d}
                {(d === "all" ? unreadCount : unreadByDepartment[d] || 0) > 0 && (
                  <span className="ml-1 font-semibold text-destructive">{d === "all" ? unreadCount : unreadByDepartment[d]}</span>
                )}
              </button>
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        {recent.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No notifications yet
          </div>
        ) : (
          recent.map((n) => (
            <DropdownMenuItem
              key={n.id}
              className="flex items-start gap-3 px-3 py-2.5 cursor-pointer"
              onClick={() => {
                if (!n.read) markAsRead(n.id);
                if (n.link) navigate(n.link);
              }}
            >
              <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-primary"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm leading-tight ${n.read ? "text-muted-foreground" : "font-medium"}`}>
                  {n.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{n.message}</p>
                <p className="text-[10px] text-muted-foreground/70 mt-1">
                  {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                </p>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
